import { useProductAccess } from "../product-access/ProductAccessContext";
import { TIER_PRODUCT_FEATURES } from "../product-access/productScope";
/**
 * @fileoverview Context React pour la gestion des accès par tier EBEA
 *
 * Ce context expose les fonctions d'accès au tier pour que tous les composants
 * puissent vérifier les permissions sans prop drilling.
 *
 * @module contexts/TierAccessContext
 * @author James (Dev) - Story 1.2
 * @version 1.0.0
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  TIER_HIERARCHY,
  TIER_META,
  FEATURE_ACCESS,
  DEFAULT_ACCESS,
  getTierIndex,
  hasTierAccess,
  getFeatureConfig,
  getTierMeta,
  getAccessibleFeatures as getAccessibleFeaturesFromConfig,
  getNextTierFeatures as getNextTierFeaturesFromConfig
} from '../config/tierConfig';

// =============================================================================
// CONTEXT CREATION
// =============================================================================

/**
 * Context pour l'accès aux informations de tier.
 * Null par défaut, doit être utilisé avec TierAccessProvider.
 *
 * @type {React.Context}
 */
const TierAccessContext = createContext(null);

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

/**
 * Provider React qui expose les fonctions d'accès au tier EBEA.
 *
 * Ce provider lit le tier depuis Redux et expose des fonctions pour:
 * - Vérifier l'accès à une feature
 * - Récupérer les informations d'une feature
 * - Lister les features accessibles
 * - Lister les features du tier supérieur
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {React.ReactNode} props.children - Composants enfants
 * @returns {JSX.Element} Provider avec le context
 *
 * @example
 * // Dans index.js ou App.js
 * <Provider store={store}>
 *   <TierAccessProvider>
 *     <App />
 *   </TierAccessProvider>
 * </Provider>
 */
export const TierAccessProvider = ({ children }) => {
  // Lecture du tier et zooId depuis Redux
  const productScope = useProductAccess();
  const zooTier = useSelector((state) => state.zoo?.zooTier);
  const zooId = useSelector((state) => state.zoo?.zooId);

  // Fallback vers 'discovery' si undefined
  const currentTier = zooTier || 'discovery';

  // Index du tier actuel dans la hiérarchie
  const currentTierIndex = getTierIndex(currentTier);

  // Métadonnées du tier actuel
  const tierMeta = getTierMeta(currentTier) || TIER_META.discovery;

  // =========================================================================
  // FONCTIONS D'ACCÈS
  // =========================================================================

  /**
   * Vérifie si l'utilisateur a accès à une feature donnée.
   *
   * @param {string} featureKey - Clé de la feature à vérifier
   * @returns {boolean} True si l'utilisateur a accès à la feature
   *
   * @example
   * const { hasAccess } = useTierAccess();
   * if (hasAccess('enrichment.analysis')) {
   *   // Afficher le contenu
   * }
   */
  const hasAccess = (featureKey) => {
    const productFeature = TIER_PRODUCT_FEATURES[featureKey];
    // A configured park follows its composition. Without composition, the product scope can only
    // withhold a feature (explicit Rafiki setting); the historical tier keeps deciding the rest.
    if (productFeature && (productScope.mode !== "legacy" || !productScope.can(productFeature))) return productScope.can(productFeature);
    const featureConfig = getFeatureConfig(featureKey);
    return hasTierAccess(currentTier, featureConfig.minTier);
  };

  /**
   * Récupère les informations complètes d'une feature.
   *
   * @param {string} featureKey - Clé de la feature
   * @returns {Object|null} Informations de la feature ou null si non trouvée
   * @property {Object} config - Configuration de la feature
   * @property {string} requiredTier - Tier minimum requis
   * @property {Object} requiredTierMeta - Métadonnées du tier requis
   * @property {boolean} hasAccess - Si l'utilisateur a accès
   * @property {number} tierDiff - Différence de tier (négatif = manque de niveaux)
   *
   * @example
   * const { getFeatureInfo } = useTierAccess();
   * const info = getFeatureInfo('enrichment.analysis');
   * if (!info.hasAccess) {
   *   console.log(`Nécessite ${info.requiredTierMeta.label}`);
   * }
   */
  const getFeatureInfo = (featureKey) => {
    const config = FEATURE_ACCESS[featureKey];

    if (!config) {
      return null;
    }

    const requiredTier = config.minTier;
    const requiredTierIndex = getTierIndex(requiredTier);
    const requiredTierMeta = getTierMeta(requiredTier);

    return {
      key: featureKey,
      config,
      requiredTier,
      requiredTierMeta,
      hasAccess: hasAccess(featureKey),
      tierDiff: currentTierIndex - requiredTierIndex
    };
  };

  /**
   * Liste toutes les features accessibles pour le tier actuel.
   *
   * @returns {string[]} Liste des clés de features accessibles
   *
   * @example
   * const { getAccessibleFeatures } = useTierAccess();
   * const features = getAccessibleFeatures();
   * // ['campaign.focalSampling', 'environmentalMonitoring', ...]
   */
  const getAccessibleFeatures = () => {
    return productScope.mode === "legacy" ? getAccessibleFeaturesFromConfig(currentTier) : Object.keys(FEATURE_ACCESS).filter(hasAccess);
  };

  /**
   * Liste les features qui seraient débloquées au tier supérieur.
   *
   * @returns {Array<{key: string, minTier: string, category: string, dummyGenerator: string|null}>}
   *
   * @example
   * const { getNextTierFeatures } = useTierAccess();
   * const nextFeatures = getNextTierFeatures();
   * // [{ key: 'enrichment.analysis', minTier: 'fullcare', ... }]
   */
  const getNextTierFeatures = () => {
    return getNextTierFeaturesFromConfig(currentTier);
  };

  // =========================================================================
  // MEMOIZATION
  // =========================================================================

  /**
   * Objet value mémorisé pour éviter les re-renders inutiles.
   * Se recalcule uniquement si currentTier ou zooId change.
   */
  const value = useMemo(
    () => ({
      // État
      currentTier,
      currentTierIndex,
      tierMeta,
      zooId,

      // Fonctions
      hasAccess,
      getFeatureInfo,
      getAccessibleFeatures,
      getNextTierFeatures,

      // Constantes exposées pour usage direct
      TIER_HIERARCHY,
      TIER_META,
      FEATURE_ACCESS
    }),
    [currentTier, currentTierIndex, tierMeta, zooId, productScope]
  );

  return (
    <TierAccessContext.Provider value={value}>
      {children}
    </TierAccessContext.Provider>
  );
};

// =============================================================================
// CUSTOM HOOK
// =============================================================================

/**
 * Hook personnalisé pour accéder au TierAccessContext.
 *
 * @returns {Object} Contexte d'accès au tier
 * @property {string} currentTier - Tier actuel ('discovery', 'essential', etc.)
 * @property {number} currentTierIndex - Index du tier (0-4)
 * @property {Object} tierMeta - Métadonnées du tier (label, icon, color)
 * @property {string} zooId - ID du zoo actuel
 * @property {Function} hasAccess - Vérifie l'accès à une feature
 * @property {Function} getFeatureInfo - Récupère les infos d'une feature
 * @property {Function} getAccessibleFeatures - Liste les features accessibles
 * @property {Function} getNextTierFeatures - Liste les features du tier suivant
 *
 * @throws {Error} Si utilisé en dehors d'un TierAccessProvider
 *
 * @example
 * const { currentTier, hasAccess, tierMeta } = useTierAccess();
 *
 * return (
 *   <div>
 *     <p>Tier actuel: {tierMeta.label}</p>
 *     {hasAccess('enrichment.analysis') ? (
 *       <EnrichmentAnalysis />
 *     ) : (
 *       <UpgradePrompt />
 *     )}
 *   </div>
 * );
 */
export const useTierAccess = () => {
  const context = useContext(TierAccessContext);

  if (context === null) {
    throw new Error(
      'useTierAccess must be used within a TierAccessProvider. ' +
        'Make sure to wrap your app with <TierAccessProvider>.'
    );
  }

  return context;
};

// =============================================================================
// EXPORTS
// =============================================================================

export { TierAccessContext };
export default TierAccessProvider;
